import { z } from "zod";
import { PERSON_GENDERS, type PersonGender } from "./competition/participants";
import type { Paginated } from "./envelope";
import { PAGE_SIZE_MAX } from "./pagination";
import type { AcCardStatus } from "./accreditation";
import type { EntryPointRef, FlightStatus, FlightSummary, TravelMode } from "./travel";

/**
 * Accommodation (M11): lodging buildings and rooms, whole-room reservations
 * per contingent, room assignments (bed slots), the front desk (arrivals,
 * check-in / out, departures) and HOTEL logins. Wire shapes only
 * (snake_case). Plan: `~/.claude/plans/m11-accommodation/PLAN.md` (§0b, §0c win).
 *
 * The database CHECKs and functions (migration `0025_accommodation`) are
 * generated from the tuples and constants below; a parity test holds them
 * equal. Rename nothing here without the migration.
 *
 * Rules both sides lean on:
 *   · The arrival window is COMPUTED on every read, never stored:
 *     eta = travel_legs.expected_at + entry_points.dwell_minutes
 *         + lodging_drive_minutes(games, entry point cluster, building cluster);
 *     window = [eta − ETA_EARLY_MINUTES, eta + ETA_LATE_MINUTES].
 *   · `room_assignments.stay` (a daterange [first night, day after last night))
 *     IS stored — the exclusion constraints need it — and derived by ONE SQL
 *     function, `fn_room_stay`: the first night is the games-time-zone date of
 *     (eta − NIGHT_CUTOFF_MINUTES), so an arrival before 06:00 keeps the
 *     previous night's bed (§0b Q1). No travel → the contingent's default
 *     dates, else games start − STAY_DEFAULT_DAYS_BEFORE_START → end +
 *     STAY_DEFAULT_DAYS_AFTER_END (§0b Q2).
 *   · Beds are slots: `bed_no` 1..beds; capacity, one sex per room per night
 *     and one bed per card per night are EXCLUDE constraints.
 *   · Only APPROVED cards get rooms.
 *   · Enum members go in VALUES, never response keys (`ok()` lower-cases
 *     all-caps keys): counts are `{ state, count }[]`, never `Record<Enum, n>`.
 *   · NOC privacy (M10 §0b): a NOC's rooming read carries only its own people
 *     — never who else shares a room or building.
 */

// ── Vocabulary ──────────────────────────────────────────────────────────────

/** `lodging_buildings.type` (`ck_lodging_buildings_type`). */
export const LODGING_BUILDING_TYPES = ["VILLAGE", "HOTEL", "OTHER"] as const;
export type LodgingBuildingType = (typeof LODGING_BUILDING_TYPES)[number];

/**
 * `room_assignments.stay_source` (`ck_room_assignments_stay_source`):
 *   TRAVEL   derived from the card's arrival / departure legs (follows them)
 *   DEFAULT  no travel: the contingent's default dates, else the games' dates (follows them)
 *   MANUAL   dates the host typed — never moves; flagged when it differs from travel
 */
export const STAY_SOURCES = ["TRAVEL", "DEFAULT", "MANUAL"] as const;
export type StaySource = (typeof STAY_SOURCES)[number];
/** The sources `fn_room_restay` re-derives. */
export const STAY_SOURCES_DERIVED = ["TRAVEL", "DEFAULT"] as const satisfies readonly StaySource[];

/** `room_assignments.checked_in_via` (`ck_room_assignments_checked_in_via`). SELF is for M13 (participant app). */
export const CHECK_IN_VIA = ["DESK", "SCAN", "SELF"] as const;
export type CheckInVia = (typeof CHECK_IN_VIA)[number];

/** A room's or reservation's sex: the person genders (`people.gender`). NULL = any. */
export const LODGING_SEXES = PERSON_GENDERS;
export type LodgingSex = PersonGender;

/**
 * A person's rooming state (Audrey V5, derived in SQL, never stored):
 *   NONE      "No bed" — APPROVED, no open assignment
 *   ASSIGNED  has a bed, not checked in
 *   IN        "Checked in"
 *   OUT       "Checked out" (the latest assignment is closed)
 */
export const ROOMING_PERSON_STATES = ["NONE", "ASSIGNED", "IN", "OUT"] as const;
export type RoomingPersonState = (typeof ROOMING_PERSON_STATES)[number];

/**
 * A room's state on a date (Audrey V5, derived in SQL — the floor map's CASE):
 *   VACANT    not reserved, nobody staying
 *   RESERVED  "Reserved, empty"
 *   PART      "Partly occupied"
 *   FULL      every bed taken
 *   OOS       "Out of service"
 * Precedence: OOS, FULL, PART, RESERVED, VACANT.
 */
export const ROOM_STATES = ["VACANT", "RESERVED", "PART", "FULL", "OOS"] as const;
export type RoomState = (typeof ROOM_STATES)[number];

/**
 * Markers on a person / assignment row (derived in SQL, never stored):
 *   STAY_DIFFERS     the stored stay is not what `fn_room_stay` gives now (a MANUAL stay, or a growth that found the bed taken)
 *   GENDER_CHANGED   `people.gender` no longer equals the assignment's `sex`
 *   CARD_WITHDRAWN   checked in, but the card left APPROVED — check them out
 *   LATE_ARRIVAL     the arrival window ends outside the building's desk hours, or after midnight (held from the previous night)
 *   OVERDUE          the first night has passed and the guest is not checked in
 *   LATE_CHECKOUT    the last night has passed and the guest is still checked in
 */
export const ACCOMMODATION_FLAGS = ["STAY_DIFFERS", "GENDER_CHANGED", "CARD_WITHDRAWN", "LATE_ARRIVAL", "OVERDUE", "LATE_CHECKOUT"] as const;
export type AccommodationFlag = (typeof ACCOMMODATION_FLAGS)[number];

/** Bulk desk actions (`POST /room-assignments/check`). UNDO_OUT takes one id. */
export const ROOM_CHECK_ACTIONS = ["IN", "OUT", "UNDO_IN", "UNDO_OUT"] as const;
export type RoomCheckAction = (typeof ROOM_CHECK_ACTIONS)[number];

/** Why one id of a desk action was skipped (`skipped[].reason`). */
export const ROOM_CHECK_REFUSALS = [
	"not_found",
	"card_not_ready",
	"stay_not_started",
	"stay_ended",
	"already_in",
	"not_in",
	"already_out",
	"checked_out_first",
] as const;
export type RoomCheckRefusal = (typeof ROOM_CHECK_REFUSALS)[number];

/** `ROOM_NOT_AVAILABLE` `details.reason` — the guard trigger's refusals, mapped from its named constraint. */
export const ROOM_UNAVAILABLE_REASONS = ["reserved_other", "out_of_service", "sex", "beds"] as const;
export type RoomUnavailableReason = (typeof ROOM_UNAVAILABLE_REASONS)[number];

/** Why `POST /room-assignments` refused before trying (the explain SELECT after 0 rows). */
export const ROOM_ASSIGN_REFUSALS = ["not_found", "not_in_scope", "card_not_approved", "no_gender", "no_dates", "no_free_bed"] as const;
export type RoomAssignRefusal = (typeof ROOM_ASSIGN_REFUSALS)[number];

/**
 * Why auto-assign skipped a person. `fn_auto_assign_rooms` answers
 * `no_gender` / `no_dates` / `no_reserved_bed`; the reporting statement
 * (`autoAssignSql`) splits `no_dates` by the end that is missing:
 * `no_arrival_date` (no first night), `no_departure_date` (no last night),
 * `no_dates` (neither).
 */
export const AUTO_ASSIGN_SKIP_REASONS = ["no_gender", "no_arrival_date", "no_departure_date", "no_dates", "no_reserved_bed"] as const;
export type AutoAssignSkipReason = (typeof AUTO_ASSIGN_SKIP_REASONS)[number];

/** Why a room of a bulk room edit was skipped (`PATCH /lodging-buildings/:id/rooms`). */
/** `in_use`: a room asked to be removed that has had a guest, a reservation or a helpdesk ticket — mark it out of service instead (2026-11-03). */
export const ROOM_EDIT_REFUSALS = ["occupied", "beds_in_use", "other_sex_staying", "in_use"] as const;
export type RoomEditRefusal = (typeof ROOM_EDIT_REFUSALS)[number];

/** Why a room of a reservation release was skipped. */
export const RESERVATION_RELEASE_REFUSALS = ["not_reserved", "occupied"] as const;
export type ReservationReleaseRefusal = (typeof RESERVATION_RELEASE_REFUSALS)[number];

/**
 * "Arriving without a room" (GMS-2): who lands on a day with no open bed —
 *   needs_room    APPROVED: a room can be assigned now
 *   not_approved  not APPROVED yet: shown greyed, read-only (no bed until approved)
 */
export const NEEDS_ROOM_STATES = ["needs_room", "not_approved"] as const;
export type NeedsRoomState = (typeof NEEDS_ROOM_STATES)[number];

/** How the desk's arrival / departure groups are keyed: a flight, else entry point + service + hour, else no travel. */
export const STAY_GROUP_KINDS = ["FLIGHT", "SERVICE", "NO_TRAVEL"] as const;
export type StayGroupKind = (typeof STAY_GROUP_KINDS)[number];

/** Sort of the contingent people list. */
export const ROOMING_PEOPLE_SORT_KEYS = ["name", "team", "room"] as const;
export type RoomingPeopleSortKey = (typeof ROOMING_PEOPLE_SORT_KEYS)[number];

// ── Time rules (pinned by the parity test against fn_room_stay / the window SQL) ──

/** The arrival window opens this long before the eta. */
export const ETA_EARLY_MINUTES = 20;
/** …and closes this long after it. */
export const ETA_LATE_MINUTES = 40;
/** Arrivals at the building before 06:00 (games time zone) count as the previous night (§0b Q1). */
export const NIGHT_CUTOFF_MINUTES = 360;
/** No travel and no contingent defaults: first night = games `starts_on` − this many days. */
export const STAY_DEFAULT_DAYS_BEFORE_START = 2;
/** …last night's morning (the stay's exclusive upper bound) = games `ends_on` + this many days. */
export const STAY_DEFAULT_DAYS_AFTER_END = 1;
/** A HOTEL login's lists drop a guest this many days after check-out (§0b Q3). */
export const HOTEL_LIST_RETENTION_DAYS = 3;
/** A building's default check-out time (`lodging_buildings.check_out_time`), `HH:MM`. */
export const LODGING_CHECK_OUT_TIME_DEFAULT = "12:00";
/** `HH:MM`, 24 h, games time zone (desk hours, check-out time). */
export const LODGING_TIME_PATTERN = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

// ── Limits ──────────────────────────────────────────────────────────────────

/** `lodging_rooms.beds` (`ck_lodging_rooms_beds`), and so `room_assignments.bed_no`. */
export const LODGING_BEDS_MIN = 1;
export const LODGING_BEDS_MAX = 6;
/** `lodging_rooms.floor` (`ck_lodging_rooms_floor`). */
export const LODGING_FLOOR_MIN = -5;
export const LODGING_FLOOR_MAX = 200;
export const LODGING_BUILDING_NAME_MAX = 120;
export const LODGING_ADDRESS_MAX = 300;
export const LODGING_CONTACT_NAME_MAX = 120;
export const LODGING_CONTACT_PHONE_MAX = 30;
export const LODGING_SORT_ORDER_MAX = 9999;
export const LODGING_BLOCK_MAX = 10;
export const LODGING_ROOM_NO_MAX = 20;
/** A building's short code (user, 2026-11-03: "Building ID" — BJV, IST-1): letters, digits, hyphen, upper-cased, unique. */
export const LODGING_BUILDING_CODE_MAX = 12;
export const LODGING_BUILDING_CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]*$/;
export const LODGING_OOS_NOTE_MAX = 300;
/** Buildings a list returns (`GET /lodging-buildings`): a venue-sized set. */
export const LODGING_BUILDINGS_LIMIT = 200;
/** Rooms one `POST /lodging-buildings/:id/rooms/generate` may create (blocks × floors × rooms per floor). */
export const LODGING_ROOMS_GENERATE_MAX = 5000;
/** Blocks one generate call may name. */
export const LODGING_BLOCKS_MAX = 26;
/** Rooms per floor one generate call may create. */
export const LODGING_ROOMS_PER_FLOOR_MAX = 200;
/** Rooms one bulk room edit / reservation release may name. */
export const LODGING_ROOMS_BULK_MAX = 500;
/** Floor-map rooms per call (one block). */
export const LODGING_FLOOR_MAP_ROOMS_MAX = 2000;
/** Ids one bulk desk action may carry. */
export const ROOM_CHECK_IDS_MAX = 300;
/** Organisations one auto-assign may run. */
export const AUTO_ASSIGN_ORGS_MAX = 60;
/** Skipped people an auto-assign result lists (the rest are counted). */
export const AUTO_ASSIGN_SKIPPED_MAX = 500;
/** Proposed beds a room suggestion lists (the rest are counted; the fingerprint covers all of them). */
export const AUTO_ASSIGN_PICKS_MAX = 5000;
/** People one room suggestion may leave out ("untick"). */
export const AUTO_ASSIGN_EXCLUDE_MAX = 2000;
/** Beds one reservation call may ask for, per sex. */
export const RESERVATION_BEDS_MAX = 5000;
/** Rows the rooming CSV may stream. */
export const ROOMING_CSV_MAX = 5000;
/** Contingents the Rooming table lists. */
export const ROOMING_CONTINGENTS_LIMIT = 500;
export const ROOMING_PEOPLE_PAGE_SIZE_DEFAULT = 50;
/** Desk groups one arrivals / departures read returns (`truncated` beyond). */
export const STAY_GROUPS_LIMIT = 300;
/** Organisations a desk group names (the rest are counted). */
export const STAY_GROUP_ORGS_MAX = 5;
export const STAY_GROUP_KEY_MAX = 200;
/** Nights a typed (MANUAL) stay may span. */
export const STAY_NIGHTS_MAX = 60;
/** Buildings one `building_ids` filter may name. */
export const BUILDING_IDS_MAX = 50;
/** The `q` search box. */
export const ACCOMMODATION_SEARCH_MAX = 100;
/** The card-number lookup box (`ACC-THA-000123`, `REG-THA-000123`, `000123`, `123`; older forms still parse). */
export const ACCOMMODATION_LOOKUP_MAX = 40;
/** `GET /accommodation/needs-room` default page size (max PAGE_SIZE_MAX). */
export const NEEDS_ROOM_PAGE_SIZE_DEFAULT = 25;
/** `GET /accommodation/needs-room/day-counts`: the widest range one call may count (the chips show a week). */
export const NEEDS_ROOM_DAY_COUNTS_RANGE_DAYS_MAX = 14;

// ── Formatting ──────────────────────────────────────────────────────────────

/**
 * A room as printed everywhere: `A-412` when the building has several blocks
 * or the block is one letter, else `412`. `blockCount` = the building's
 * distinct non-empty blocks. The SQL twin (0025) follows the same rule.
 */
export function formatRoomNo(block: string | null | undefined, roomNo: string, blockCount = 1): string {
	const b = block?.trim() ?? "";
	if (b === "") return roomNo;
	return blockCount > 1 || b.length === 1 ? `${b}-${roomNo}` : roomNo;
}

/**
 * A generated room's number (`POST /lodging-buildings/:id/rooms/generate`):
 * the floor, then the room's index on that floor padded to at least two
 * digits (`412` = floor 4, room 12; `1203` with 100+ rooms a floor → `12003`).
 * Basement floors read `B1`… (`B105`). The SQL generator follows the same rule.
 */
export function generatedRoomNo(floor: number, index: number, roomsPerFloor: number): string {
	const width = Math.max(2, String(roomsPerFloor).length);
	const f = floor < 0 ? `B${-floor}` : String(floor);
	return `${f}${String(index).padStart(width, "0")}`;
}

// ── Messages (message-factory pattern) ──────────────────────────────────────
// English defaults, so the API and the default exports keep the English wire
// copy while the web app passes translated copy (namespace `accommodation`).
// Every string is trimmed BEFORE its length check (CLAUDE.md bug 5).

export interface AccommodationFormMessages {
	required: string;
	textTooLong: string;
	nameRequired: string;
	dateInvalid: string;
	/** `stay_to` not after `stay_from` (a stay is at least one night). */
	stayRangeInvalid: string;
	stayTooLong: string;
	/** Only one of `stay_from` / `stay_to`. */
	stayBothOrNeither: string;
	timeInvalid: string;
	/** Only one of desk opens / closes (both empty = 24 h). */
	deskHoursBothOrNeither: string;
	numberInvalid: string;
	bedsInvalid: string;
	floorInvalid: string;
	/** floor_to before floor_from. */
	floorRangeInvalid: string;
	blockInvalid: string;
	roomNoInvalid: string;
	/** blocks × floors × rooms per floor is over LODGING_ROOMS_GENERATE_MAX. */
	tooManyRooms: string;
	idsDuplicate: string;
	idsTooMany: string;
	nothingToChange: string;
	/** A bulk room edit names both `lodging_room_ids` and a block / floors, or neither. */
	selectionInvalid: string;
	/** Asked for no beds at all. */
	bedsRequired: string;
	/** UNDO_OUT takes exactly one assignment. */
	undoOutOne: string;
	/** Default dates: depart_on not after arrive_on, or only one of them. */
	defaultsInvalid: string;
}

export const enAccommodationFormMessages: AccommodationFormMessages = {
	required: "Required",
	textTooLong: "Too long",
	nameRequired: "Enter a name",
	dateInvalid: "Enter a valid date",
	stayRangeInvalid: "The check-out date must be after the first night",
	stayTooLong: `At most ${STAY_NIGHTS_MAX} nights`,
	stayBothOrNeither: "Enter both dates, or neither to follow travel",
	timeInvalid: "Enter a time like 07:00",
	deskHoursBothOrNeither: "Set both desk hours, or neither for 24 hours",
	numberInvalid: "Enter a whole number in range",
	bedsInvalid: `Beds: ${LODGING_BEDS_MIN} to ${LODGING_BEDS_MAX}`,
	floorInvalid: `Floors: ${LODGING_FLOOR_MIN} to ${LODGING_FLOOR_MAX}`,
	floorRangeInvalid: "The last floor must be on or above the first",
	blockInvalid: "Use letters, digits or hyphens",
	roomNoInvalid: "Use letters, digits or hyphens",
	tooManyRooms: `At most ${LODGING_ROOMS_GENERATE_MAX} rooms at once`,
	idsDuplicate: "Listed twice",
	idsTooMany: "Too many at once",
	nothingToChange: "Nothing to change",
	selectionInvalid: "Pick rooms, or a block and floors",
	bedsRequired: "Ask for at least one bed",
	undoOutOne: "Undo one check-out at a time",
	defaultsInvalid: "The departure date must be after the arrival date",
};

type AM = AccommodationFormMessages;

// ── Field builders (private) ────────────────────────────────────────────────

const uuid = z.string().uuid();
const isRealDate = (v: string) => {
	const d = new Date(`${v}T00:00:00Z`);
	return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};
const dateField = (m: { dateInvalid: string }) => z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, m.dateInvalid).refine(isRealDate, m.dateInvalid);
/** Whole days from `a` to `b` (YYYY-MM-DD). */
export const daysBetweenDates = (a: string, b: string): number => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
const timeField = (m: { timeInvalid: string }) => z.string().trim().regex(LODGING_TIME_PATTERN, m.timeInvalid);
/** Optional text: trimmed, then ≤ max; blank, null or omitted → null. */
const nullableText = (m: { textTooLong: string }, max: number) =>
	z
		.string()
		.trim()
		.max(max, m.textTooLong)
		.nullable()
		.optional()
		.transform((v) => (v === undefined || v === null || v === "" ? null : v));
/** A PATCH text field: omitted leaves it alone, null or blank clears it. */
const patchText = (m: { textTooLong: string }, max: number) =>
	z
		.string()
		.trim()
		.max(max, m.textTooLong)
		.transform((v) => (v === "" ? null : v))
		.nullable()
		.optional();
const int = (m: { numberInvalid: string }, min: number, max: number, message = m.numberInvalid) =>
	z.number({ invalid_type_error: message }).int(message).min(min, message).max(max, message);
const uniqueIds = (ids: readonly string[]) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length;
/** `a,b,c` off the query string → a de-duplicated list of uuids (≤ max). */
const uuidList = (max: number) =>
	z
		.string()
		.max(max * 37)
		.transform((v) =>
			v
				.split(",")
				.map((s) => s.trim())
				.filter((s) => s.length > 0),
		)
		.pipe(z.array(uuid).min(1).max(max))
		.transform((list) => [...new Set(list.map((id) => id.toLowerCase()))]);
/** `a,b,c` off the query string → a de-duplicated list of `values`. */
const enumList = <T extends readonly [string, ...string[]]>(values: T) =>
	z
		.string()
		.max(200)
		.transform((v) =>
			v
				.split(",")
				.map((s) => s.trim())
				.filter((s) => s.length > 0),
		)
		.pipe(z.array(z.enum(values)).min(1))
		.transform((list) => [...new Set(list)]);
/** A block / room label: letters, digits, hyphen; trimmed then upper-cased. */
const LABEL_PATTERN = /^[A-Za-z0-9][A-Za-z0-9-]*$/;
const blockField = (m: AM) =>
	z
		.string()
		.trim()
		.min(1, m.blockInvalid)
		.max(LODGING_BLOCK_MAX, m.blockInvalid)
		.regex(LABEL_PATTERN, m.blockInvalid)
		.transform((v) => v.toUpperCase());
const searchQ = z.string().trim().min(1).max(ACCOMMODATION_SEARCH_MAX);
const pageField = z.coerce.number().int().min(1).default(1);
const pageSizeField = (dflt: number) => z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(dflt);

// ── Params ──────────────────────────────────────────────────────────────────

/** `/lodging-buildings/:id`, `/room-assignments/:id`. */
export const accommodationIdParam = z.object({ id: uuid });
/** `/accommodation/contingents/:org/…` — the responsible organisation's id. */
export const contingentParam = z.object({ org: uuid });

// ── Buildings and rooms (S2) ────────────────────────────────────────────────

function lodgingBuildingFields(m: AM) {
	return {
		name: z.string().trim().min(1, m.nameRequired).max(LODGING_BUILDING_NAME_MAX, m.textTooLong),
		/** The building's short code; blank = none. Upper-cased. */
		code: z
			.string()
			.trim()
			.transform((v) => v.toUpperCase())
			.pipe(z.union([z.literal(""), z.string().max(LODGING_BUILDING_CODE_MAX, m.blockInvalid).regex(LODGING_BUILDING_CODE_PATTERN, m.blockInvalid)]))
			.transform((v) => (v === "" ? null : v))
			.nullable()
			.optional(),
		type: z.enum(LODGING_BUILDING_TYPES),
		cluster_id: uuid,
		address: nullableText(m, LODGING_ADDRESS_MAX),
		contact_name: nullableText(m, LODGING_CONTACT_NAME_MAX),
		contact_phone: nullableText(m, LODGING_CONTACT_PHONE_MAX),
		/** `HH:MM`, games time zone. Both null = a 24-hour desk; overnight (closes before opens) is allowed. */
		desk_opens_at: timeField(m).nullable().optional(),
		desk_closes_at: timeField(m).nullable().optional(),
		check_out_time: timeField(m).optional(),
		is_active: z.boolean().optional(),
		sort_order: int(m, 0, LODGING_SORT_ORDER_MAX).optional(),
	};
}
const deskHoursPaired = (v: { desk_opens_at?: string | null; desk_closes_at?: string | null }) =>
	(v.desk_opens_at == null) === (v.desk_closes_at == null);

/** `POST /lodging-buildings` (S2). Admins or `accommodation.manage`. */
export function makeCreateLodgingBuildingBody(m: AM = enAccommodationFormMessages) {
	return z
		.object(lodgingBuildingFields(m))
		.refine(deskHoursPaired, { message: m.deskHoursBothOrNeither, path: ["desk_closes_at"] })
		.transform((v) => ({
			...v,
			desk_opens_at: v.desk_opens_at ?? null,
			desk_closes_at: v.desk_closes_at ?? null,
			code: v.code ?? null,
			check_out_time: v.check_out_time ?? LODGING_CHECK_OUT_TIME_DEFAULT,
			is_active: v.is_active ?? true,
			sort_order: v.sort_order ?? 0,
		}));
}
export const createLodgingBuildingBody = makeCreateLodgingBuildingBody();
export type CreateLodgingBuildingBody = z.input<typeof createLodgingBuildingBody>;
export type CreateLodgingBuildingInput = z.output<typeof createLodgingBuildingBody>;

/**
 * `PATCH /lodging-buildings/:id` (S2) — any subset; null / blank clears a
 * nullable text. Desk hours change together (send both, or both null).
 * Changing `cluster_id` re-derives stays (0025 trigger).
 */
export function makePatchLodgingBuildingBody(m: AM = enAccommodationFormMessages) {
	const f = lodgingBuildingFields(m);
	return z
		.object({
			name: f.name.optional(),
			code: f.code,
			type: f.type.optional(),
			cluster_id: uuid.optional(),
			address: patchText(m, LODGING_ADDRESS_MAX),
			contact_name: patchText(m, LODGING_CONTACT_NAME_MAX),
			contact_phone: patchText(m, LODGING_CONTACT_PHONE_MAX),
			desk_opens_at: f.desk_opens_at,
			desk_closes_at: f.desk_closes_at,
			check_out_time: f.check_out_time,
			is_active: f.is_active,
			sort_order: f.sort_order,
		})
		.refine((v) => (v.desk_opens_at === undefined) === (v.desk_closes_at === undefined) && (v.desk_opens_at === undefined || deskHoursPaired(v)), {
			message: m.deskHoursBothOrNeither,
			path: ["desk_closes_at"],
		})
		.refine((v) => Object.values(v).some((x) => x !== undefined), { message: m.nothingToChange });
}
export const patchLodgingBuildingBody = makePatchLodgingBuildingBody();
export type PatchLodgingBuildingBody = z.input<typeof patchLodgingBuildingBody>;

/**
 * `POST /lodging-buildings/:id/rooms/generate` (S2): blocks × floors × rooms
 * per floor, numbered by `generatedRoomNo`, in one INSERT … SELECT. Rooms that
 * already exist are skipped (`ON CONFLICT DO NOTHING`, counted). `blocks`
 * empty = one block with no letter.
 */
export function makeGenerateRoomsBody(m: AM = enAccommodationFormMessages) {
	return z
		.object({
			blocks: z
				.array(blockField(m))
				.max(LODGING_BLOCKS_MAX, m.idsTooMany)
				.default([])
				.refine((b) => new Set(b).size === b.length, { message: m.idsDuplicate }),
			floor_from: int(m, LODGING_FLOOR_MIN, LODGING_FLOOR_MAX, m.floorInvalid),
			floor_to: int(m, LODGING_FLOOR_MIN, LODGING_FLOOR_MAX, m.floorInvalid),
			rooms_per_floor: int(m, 1, LODGING_ROOMS_PER_FLOOR_MAX),
			beds: int(m, LODGING_BEDS_MIN, LODGING_BEDS_MAX, m.bedsInvalid),
			sex_restriction: z.enum(LODGING_SEXES).nullable().default(null),
		})
		.refine((v) => v.floor_to >= v.floor_from, { message: m.floorRangeInvalid, path: ["floor_to"] })
		.refine((v) => Math.max(1, v.blocks.length) * (v.floor_to - v.floor_from + 1) * v.rooms_per_floor <= LODGING_ROOMS_GENERATE_MAX, {
			message: m.tooManyRooms,
			path: ["rooms_per_floor"],
		});
}
export const generateRoomsBody = makeGenerateRoomsBody();
export type GenerateRoomsBody = z.input<typeof generateRoomsBody>;
export type GenerateRoomsInput = z.output<typeof generateRoomsBody>;

/**
 * `PATCH /lodging-buildings/:id/rooms` (S2) — a bulk room edit: EITHER
 * `lodging_room_ids` (≤ LODGING_ROOMS_BULK_MAX) OR a `block` + `floors` (her
 * "women's floors"). Rooms that cannot take the change are skipped with a
 * reason (ROOM_EDIT_REFUSALS), the rest are updated in ONE statement.
 */
export function makePatchRoomsBody(m: AM = enAccommodationFormMessages) {
	return z
		.object({
			lodging_room_ids: z.array(uuid).min(1).max(LODGING_ROOMS_BULK_MAX, m.idsTooMany).refine(uniqueIds, { message: m.idsDuplicate }).optional(),
			block: blockField(m).nullable().optional(),
			floors: z
				.array(int(m, LODGING_FLOOR_MIN, LODGING_FLOOR_MAX, m.floorInvalid))
				.min(1)
				.max(LODGING_FLOOR_MAX - LODGING_FLOOR_MIN + 1)
				.refine((f) => new Set(f).size === f.length, { message: m.idsDuplicate })
				.optional(),
			sex_restriction: z.enum(LODGING_SEXES).nullable().optional(),
			out_of_service: z.boolean().optional(),
			oos_note: patchText(m, LODGING_OOS_NOTE_MAX),
			beds: int(m, LODGING_BEDS_MIN, LODGING_BEDS_MAX, m.bedsInvalid).optional(),
		})
		.refine((v) => (v.lodging_room_ids !== undefined) !== (v.floors !== undefined), { message: m.selectionInvalid, path: ["lodging_room_ids"] })
		.refine((v) => v.lodging_room_ids === undefined || v.block === undefined, { message: m.selectionInvalid, path: ["block"] })
		.refine((v) => v.sex_restriction !== undefined || v.out_of_service !== undefined || v.oos_note !== undefined || v.beds !== undefined, {
			message: m.nothingToChange,
		});
}
export const patchRoomsBody = makePatchRoomsBody();

/**
 * `PUT /lodging-buildings/:id/floors` (user, 2026-11-03: "let them edit the
 * floor and each unit room number") — ONE floor of one block as it should
 * be, every room with its real values: kept rooms carry their id, new ones
 * none, a room left out is removed — only if it was never used (else
 * skipped `in_use`: mark it out of service). `new_floor` moves the floor.
 * Room numbers are any text, unique in the building and block; a number
 * taken by a room NOT on this floor writes nothing and comes back in `taken`.
 * Rooms the guests do not allow (beds in use, another sex staying, occupied)
 * are skipped with their reason, the rest saved — ONE statement.
 */
export function makeSaveFloorBody(m: AM = enAccommodationFormMessages) {
	return z.object({
		block: blockField(m).nullable(),
		floor: int(m, LODGING_FLOOR_MIN, LODGING_FLOOR_MAX, m.floorInvalid),
		new_floor: int(m, LODGING_FLOOR_MIN, LODGING_FLOOR_MAX, m.floorInvalid).optional(),
		rooms: z
			.array(
				z.object({
					lodging_room_id: uuid.optional(),
					room_no: z.string().trim().min(1, m.required).max(LODGING_ROOM_NO_MAX, m.textTooLong),
					beds: int(m, LODGING_BEDS_MIN, LODGING_BEDS_MAX, m.bedsInvalid),
					sex_restriction: z.enum(LODGING_SEXES).nullable(),
					out_of_service: z.boolean(),
					oos_note: nullableText(m, LODGING_OOS_NOTE_MAX),
				}),
			)
			.max(LODGING_ROOMS_PER_FLOOR_MAX, m.idsTooMany)
			.refine((rs) => new Set(rs.map((r) => r.room_no.toUpperCase())).size === rs.length, { message: m.idsDuplicate })
			.refine((rs) => uniqueIds(rs.flatMap((r) => (r.lodging_room_id ? [r.lodging_room_id] : []))), { message: m.idsDuplicate }),
	});
}
export const saveFloorBody = makeSaveFloorBody();
export type SaveFloorBody = z.input<typeof saveFloorBody>;
export type SaveFloorInput = z.output<typeof saveFloorBody>;

/** `PUT /lodging-buildings/:id/floors`. `taken` not empty = nothing was written. */
export interface FloorSaveResult {
	created: number;
	updated: number;
	removed: number;
	skipped: Array<{ lodging_room_id: string; room_label: string; reason: RoomEditRefusal }>;
	/** Room numbers already used by another room of the building and block (case as stored). */
	taken: string[];
}
export type PatchRoomsBody = z.input<typeof patchRoomsBody>;

/** `GET /lodging-buildings?games_id=&date=` (S2): ≤ LODGING_BUILDINGS_LIMIT rows, occupancy on `date` (default: today in the games' time zone). */
export function makeLodgingBuildingsQuery(m: AM = enAccommodationFormMessages) {
	return z.object({
		games_id: uuid,
		date: dateField(m).optional(),
		is_active: z.enum(["true", "false"]).optional(),
	});
}
export const lodgingBuildingsQuery = makeLodgingBuildingsQuery();
export type LodgingBuildingsQuery = z.output<typeof lodgingBuildingsQuery>;

/** `GET /lodging-buildings/:id/floor-map?games_id=&date=&block=` (S2). `block` omitted = the first block. */
export function makeFloorMapQuery(m: AM = enAccommodationFormMessages) {
	return z.object({
		games_id: uuid,
		date: dateField(m).optional(),
		block: blockField(m).optional(),
	});
}
export const floorMapQuery = makeFloorMapQuery();
export type FloorMapQuery = z.output<typeof floorMapQuery>;

/** `GET /lodging-rooms/:id?games_id=&date=` (S2): the room, its reservation, occupants with stays overlapping [date, ∞). */
export function makeLodgingRoomQuery(m: AM = enAccommodationFormMessages) {
	return z.object({ games_id: uuid, date: dateField(m).optional() });
}
export const lodgingRoomQuery = makeLodgingRoomQuery();
export type LodgingRoomQuery = z.output<typeof lodgingRoomQuery>;

// ── Reservations and stay defaults (S2) ─────────────────────────────────────

/**
 * `POST /accommodation/reservations` (S2) — reserve whole rooms in one
 * building for a contingent: enough rooms for `beds_female` women's beds and
 * `beds_male` men's beds (women's rooms first), `FOR UPDATE SKIP LOCKED`.
 * Answers `{ rooms, beds, short }`.
 */
export function makeReserveRoomsBody(m: AM = enAccommodationFormMessages) {
	return z
		.object({
			games_id: uuid,
			responsible_organization_id: uuid,
			lodging_building_id: uuid,
			beds_male: int(m, 0, RESERVATION_BEDS_MAX).default(0),
			beds_female: int(m, 0, RESERVATION_BEDS_MAX).default(0),
		})
		.refine((v) => v.beds_male + v.beds_female > 0, { message: m.bedsRequired, path: ["beds_male"] });
}
export const reserveRoomsBody = makeReserveRoomsBody();
export type ReserveRoomsBody = z.input<typeof reserveRoomsBody>;

/** `POST /accommodation/reservations/release` (S2): rooms with no open assignment of that games are released; the rest are skipped. */
export function makeReleaseReservationsBody(m: AM = enAccommodationFormMessages) {
	return z.object({
		games_id: uuid,
		lodging_room_ids: z.array(uuid).min(1).max(LODGING_ROOMS_BULK_MAX, m.idsTooMany).refine(uniqueIds, { message: m.idsDuplicate }),
	});
}
export const releaseReservationsBody = makeReleaseReservationsBody();
export type ReleaseReservationsBody = z.input<typeof releaseReservationsBody>;

/**
 * `PUT /accommodation/contingents/:org/stay-defaults` (S2) — the
 * contingent's default dates (§0b Q2) for people with no travel; both null
 * clears them. `depart_on` is the check-out day (exclusive upper bound).
 * The 0025 trigger re-derives DEFAULT stays.
 */
export function makePutStayDefaultsBody(m: AM = enAccommodationFormMessages) {
	return z
		.object({
			games_id: uuid,
			arrive_on: dateField(m).nullable(),
			depart_on: dateField(m).nullable(),
		})
		.refine((v) => (v.arrive_on === null) === (v.depart_on === null), { message: m.defaultsInvalid, path: ["depart_on"] })
		.refine((v) => v.arrive_on === null || v.depart_on === null || daysBetweenDates(v.arrive_on, v.depart_on) > 0, {
			message: m.defaultsInvalid,
			path: ["depart_on"],
		});
}
export const putStayDefaultsBody = makePutStayDefaultsBody();
export type PutStayDefaultsBody = z.input<typeof putStayDefaultsBody>;

// ── Rooming (S3) ────────────────────────────────────────────────────────────

/** `GET /accommodation/summary?games_id=&date=&building_ids=` (S3). */
export function makeAccommodationSummaryQuery(m: AM = enAccommodationFormMessages) {
	return z.object({
		games_id: uuid,
		date: dateField(m).optional(),
		building_ids: uuidList(BUILDING_IDS_MAX).optional(),
	});
}
export const accommodationSummaryQuery = makeAccommodationSummaryQuery();
export type AccommodationSummaryQuery = z.output<typeof accommodationSummaryQuery>;

/** `GET /accommodation/contingents?games_id=` (S3): one row per organisation in scope, ≤ ROOMING_CONTINGENTS_LIMIT. */
export const roomingContingentsQuery = z.object({ games_id: uuid });
export type RoomingContingentsQuery = z.output<typeof roomingContingentsQuery>;

/**
 * `GET /accommodation/contingents/:org/people` (S3, also the NOC's read-only
 * rooming) — APPROVED cards of the organisation in scope, with their open
 * assignment (else the latest). Offset paging with `count(*) OVER()` (one
 * organisation is bounded); `page_size` defaults to
 * ROOMING_PEOPLE_PAGE_SIZE_DEFAULT, ≤ PAGE_SIZE_MAX.
 */
export function makeRoomingPeopleQuery(_m: AM = enAccommodationFormMessages) {
	return z.object({
		games_id: uuid,
		state: enumList(ROOMING_PERSON_STATES).optional(),
		gender: z.enum(PERSON_GENDERS).optional(),
		lodging_building_id: uuid.optional(),
		q: searchQ.optional(),
		sort: z.enum(ROOMING_PEOPLE_SORT_KEYS).default("name"),
		page: pageField,
		page_size: pageSizeField(ROOMING_PEOPLE_PAGE_SIZE_DEFAULT),
	});
}
export const roomingPeopleQuery = makeRoomingPeopleQuery();
export type RoomingPeopleQuery = z.output<typeof roomingPeopleQuery>;
export type RoomingPeopleQueryInput = z.input<typeof roomingPeopleQuery>;

/** `GET /accommodation/contingents/:org/people.csv` (S3): the same WHERE, streamed, ≤ ROOMING_CSV_MAX rows. */
export const roomingPeopleCsvQuery = roomingPeopleQuery.omit({ page: true, page_size: true });
export type RoomingPeopleCsvQuery = z.output<typeof roomingPeopleCsvQuery>;

/**
 * Rooms are SUGGESTED, never auto-assigned (user, 2026-11-03: "auto assign is
 * sensitive and dangerous"). `POST /accommodation/auto-assign/suggest` runs
 * `fn_auto_assign_rooms` per organisation and ROLLS BACK, returning the picks
 * and one fingerprint per organisation; `POST /accommodation/auto-assign`
 * runs it again for the same people and keeps an organisation only when its
 * picks still equal the reviewed fingerprint (else that organisation is
 * rolled back and reported `changed`). `exclude_card_ids` = the people the
 * reviewer unticked — send the same list to both calls.
 */
export function makeRoomAutoAssignSuggestBody(m: AM = enAccommodationFormMessages) {
	return z.object({
		games_id: uuid,
		responsible_organization_ids: z.array(uuid).min(1).max(AUTO_ASSIGN_ORGS_MAX, m.idsTooMany).refine(uniqueIds, { message: m.idsDuplicate }),
		exclude_card_ids: z.array(uuid).max(AUTO_ASSIGN_EXCLUDE_MAX, m.idsTooMany).default([]),
	});
}
export const roomAutoAssignSuggestBody = makeRoomAutoAssignSuggestBody();
export type RoomAutoAssignSuggestBody = z.input<typeof roomAutoAssignSuggestBody>;

/** `POST /accommodation/auto-assign`: the suggestion's body plus the fingerprint of each organisation reviewed. */
export function makeRoomAutoAssignBody(m: AM = enAccommodationFormMessages) {
	return makeRoomAutoAssignSuggestBody(m).extend({
		fingerprints: z.record(uuid, z.string().trim().min(1).max(128)),
	});
}
export const roomAutoAssignBody = makeRoomAutoAssignBody();
export type RoomAutoAssignBody = z.input<typeof roomAutoAssignBody>;

// ── Desk reads (S3) ─────────────────────────────────────────────────────────

/**
 * `GET /accommodation/arrivals` and `/departures` (S3): the desk's groups for
 * one day (default: today in the games' time zone) in the visible buildings
 * (`lodging_building_ids` omitted = all visible). ≤ STAY_GROUPS_LIMIT groups,
 * `truncated` beyond.
 */
export function makeDeskDayQuery(m: AM = enAccommodationFormMessages) {
	return z.object({
		games_id: uuid,
		date: dateField(m).optional(),
		lodging_building_ids: uuidList(BUILDING_IDS_MAX).optional(),
	});
}
export const deskDayQuery = makeDeskDayQuery();
export type DeskDayQuery = z.output<typeof deskDayQuery>;

/**
 * `GET /accommodation/needs-room` (GMS-2) — the people whose ARRIVAL leg falls
 * on `date` (the games' zone; its expected time's day, else its travel date —
 * A&D's day rule) and who hold no open bed, in one `state`. Host only
 * (admins, OPERATION with `accommodation.manage` or `accommodation.desk`),
 * cards in scope. Keyset on the printed name (`cursor` = `next_cursor`).
 * `date` absent = today in the games' zone.
 */
export function makeNeedsRoomQuery(m: AM = enAccommodationFormMessages) {
	return z.object({
		games_id: uuid,
		date: dateField(m).optional(),
		state: z.enum(NEEDS_ROOM_STATES).default("needs_room"),
		responsible_organization_id: uuid.optional(),
		cursor: z.string().min(1).max(1200).optional(),
		limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(NEEDS_ROOM_PAGE_SIZE_DEFAULT),
	});
}
export const needsRoomQuery = makeNeedsRoomQuery();
export type NeedsRoomQuery = z.output<typeof needsRoomQuery>;
export type NeedsRoomQueryInput = z.input<typeof needsRoomQuery>;

/**
 * `GET /accommodation/needs-room/day-counts?games_id=&from=&to=` — per day, how
 * many arrive without a room (both states), for the day chips' badges. ONE
 * statement over the same scan, so a day's numbers = that day's list totals.
 * `to` defaults to `from`; ≤ NEEDS_ROOM_DAY_COUNTS_RANGE_DAYS_MAX days.
 */
export function makeNeedsRoomDayCountsQuery(m: AM = enAccommodationFormMessages) {
	return z
		.object({ games_id: uuid, from: dateField(m), to: dateField(m).optional() })
		.superRefine((v, ctx) => {
			if (v.to === undefined) return;
			const days = daysBetweenDates(v.from, v.to);
			if (days < 0) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: "The end date must be on or after the start date" });
			else if (days + 1 > NEEDS_ROOM_DAY_COUNTS_RANGE_DAYS_MAX)
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: `At most ${NEEDS_ROOM_DAY_COUNTS_RANGE_DAYS_MAX} days` });
		});
}
export const needsRoomDayCountsQuery = makeNeedsRoomDayCountsQuery();
export type NeedsRoomDayCountsQuery = z.output<typeof needsRoomDayCountsQuery>;

/** `GET /accommodation/arrivals/people` and `/departures/people` (S3): one group's guests, offset ≤ PAGE_SIZE_MAX. */
export function makeDeskGroupPeopleQuery(m: AM = enAccommodationFormMessages) {
	return makeDeskDayQuery(m).extend({
		group_key: z.string().trim().min(1).max(STAY_GROUP_KEY_MAX),
		page: pageField,
		page_size: pageSizeField(PAGE_SIZE_MAX),
	});
}
export const deskGroupPeopleQuery = makeDeskGroupPeopleQuery();
export type DeskGroupPeopleQuery = z.output<typeof deskGroupPeopleQuery>;

/** `GET /accommodation/in-house` (S3, HOTEL and desks): checked in, not out. Offset (one building is bounded). */
export function makeInHouseQuery(_m: AM = enAccommodationFormMessages) {
	return z.object({
		games_id: uuid,
		lodging_building_ids: uuidList(BUILDING_IDS_MAX).optional(),
		q: searchQ.optional(),
		page: pageField,
		page_size: pageSizeField(ROOMING_PEOPLE_PAGE_SIZE_DEFAULT),
	});
}
export const inHouseQuery = makeInHouseQuery();
export type InHouseQuery = z.output<typeof inHouseQuery>;

// ── Room assignments (S3) ───────────────────────────────────────────────────

/** `stay_from` = the first night; `stay_to` = the check-out day (the daterange's exclusive upper bound). */
function stayPair<T extends z.ZodRawShape>(m: AM, shape: T) {
	return z
		.object({ ...shape, stay_from: dateField(m).optional(), stay_to: dateField(m).optional() })
		.refine((v) => (v.stay_from === undefined) === (v.stay_to === undefined), { message: m.stayBothOrNeither, path: ["stay_to"] })
		.refine((v) => v.stay_from === undefined || v.stay_to === undefined || daysBetweenDates(v.stay_from, v.stay_to) >= 1, {
			message: m.stayRangeInvalid,
			path: ["stay_to"],
		})
		.refine((v) => v.stay_from === undefined || v.stay_to === undefined || daysBetweenDates(v.stay_from, v.stay_to) <= STAY_NIGHTS_MAX, {
			message: m.stayTooLong,
			path: ["stay_to"],
		});
}

/**
 * `POST /room-assignments` (S3): give an APPROVED card a bed. `bed_no`
 * omitted = the lowest bed free for the whole stay; dates omitted = derived
 * (`fn_room_stay`, stay_source TRAVEL / DEFAULT), given = MANUAL. Refusals:
 * BED_TAKEN, ROOM_SEX_CONFLICT, ALREADY_HAS_ROOM, ROOM_NOT_AVAILABLE
 * (`details.reason`), STAY_DATES_MISSING.
 */
export function makeCreateRoomAssignmentBody(m: AM = enAccommodationFormMessages) {
	return stayPair(m, {
		ac_card_id: uuid,
		lodging_room_id: uuid,
		bed_no: int(m, LODGING_BEDS_MIN, LODGING_BEDS_MAX, m.bedsInvalid).optional(),
	});
}
export const createRoomAssignmentBody = makeCreateRoomAssignmentBody();
export type CreateRoomAssignmentBody = z.input<typeof createRoomAssignmentBody>;

/**
 * `PATCH /room-assignments/:id` (S3): EITHER typed dates (→ MANUAL) OR
 * `follow_travel: true` (back to derived dates).
 */
export function makePatchRoomAssignmentBody(m: AM = enAccommodationFormMessages) {
	return z.union([
		z
			.object({ stay_from: dateField(m), stay_to: dateField(m) })
			.strict()
			.refine((v) => daysBetweenDates(v.stay_from, v.stay_to) >= 1, { message: m.stayRangeInvalid, path: ["stay_to"] })
			.refine((v) => daysBetweenDates(v.stay_from, v.stay_to) <= STAY_NIGHTS_MAX, { message: m.stayTooLong, path: ["stay_to"] }),
		z.object({ follow_travel: z.literal(true) }).strict(),
	]);
}
export const patchRoomAssignmentBody = makePatchRoomAssignmentBody();
export type PatchRoomAssignmentBody = z.input<typeof patchRoomAssignmentBody>;

/** `POST /room-assignments/:id/move` (S3): not checked in → UPDATE; checked in → close tonight + a new row (A6). */
export function makeMoveRoomAssignmentBody(m: AM = enAccommodationFormMessages) {
	return z.object({
		lodging_room_id: uuid,
		bed_no: int(m, LODGING_BEDS_MIN, LODGING_BEDS_MAX, m.bedsInvalid).optional(),
	});
}
export const moveRoomAssignmentBody = makeMoveRoomAssignmentBody();
export type MoveRoomAssignmentBody = z.input<typeof moveRoomAssignmentBody>;

/**
 * `POST /room-assignments/check` (S3): one bulk desk action over ≤
 * ROOM_CHECK_IDS_MAX assignments — one guarded UPDATE + one refusal SELECT.
 * UNDO_OUT takes exactly one id (it re-grows the stay and may hit BED_TAKEN).
 * `via` (check-in only) defaults to DESK; SELF is not accepted here (M13).
 */
export function makeRoomAssignmentCheckBody(m: AM = enAccommodationFormMessages) {
	return z
		.object({
			action: z.enum(ROOM_CHECK_ACTIONS),
			room_assignment_ids: z.array(uuid).min(1).max(ROOM_CHECK_IDS_MAX, m.idsTooMany).refine(uniqueIds, { message: m.idsDuplicate }),
			via: z.enum(["DESK", "SCAN"] as const satisfies readonly CheckInVia[]).default("DESK"),
		})
		.refine((v) => v.action !== "UNDO_OUT" || v.room_assignment_ids.length === 1, { message: m.undoOutOne, path: ["room_assignment_ids"] });
}
export const roomAssignmentCheckBody = makeRoomAssignmentCheckBody();
export type RoomAssignmentCheckBody = z.input<typeof roomAssignmentCheckBody>;

/** `GET /room-assignments/lookup?ac_no=` (S3, the desk scan): `ACC-THA-000123`, `000123` or `123`, older forms too (parse with `parseCardNumberQuery`). */
export const roomAssignmentLookupQuery = z.object({
	ac_no: z.string().trim().min(1).max(ACCOMMODATION_LOOKUP_MAX),
	games_id: uuid.optional(),
});
export type RoomAssignmentLookupQuery = z.output<typeof roomAssignmentLookupQuery>;

// ── Responses ───────────────────────────────────────────────────────────────

export interface LodgingBuildingRef {
	lodging_building_id: string;
	name: string;
	type: LodgingBuildingType;
}

/** `GET /lodging-buildings/options`: pickers (the ticket form, the group's Hotels section). */
export interface LodgingBuildingOption extends LodgingBuildingRef {
	is_active: boolean;
}

/** One row of `GET /lodging-buildings`. Occupancy is on the query's `date`. */
export interface LodgingBuildingListItem extends LodgingBuildingRef {
	/** The building's short code ("Building ID"); null = none set. */
	code: string | null;
	cluster: { cluster_id: string; code: string; name: string };
	address: string | null;
	contact_name: string | null;
	contact_phone: string | null;
	/** `HH:MM`, games time zone; both null = a 24-hour desk. */
	desk_opens_at: string | null;
	desk_closes_at: string | null;
	check_out_time: string;
	is_active: boolean;
	sort_order: number;
	/** Distinct blocks, sorted; `[]` = no blocks. */
	blocks: string[];
	rooms: number;
	beds: number;
	out_of_service_rooms: number;
	out_of_service_beds: number;
	/** Beds in rooms reserved for this games. */
	reserved_beds: number;
	occupied_beds: number;
	created_at: string;
	updated_at: string;
}

/**
 * `POST` / `PATCH /lodging-buildings` response: the list row without its two
 * games-bound counts (a write names no games). Derived, so the two never drift.
 */
export type LodgingBuildingConfig = Omit<LodgingBuildingListItem, "reserved_beds" | "occupied_beds">;

export interface GenerateRoomsResult {
	created: number;
	/** Already there (same block + number): left as they were. */
	skipped: number;
}

export interface RoomEditResult {
	updated: number;
	skipped: Array<{ lodging_room_id: string; room_label: string; reason: RoomEditRefusal }>;
}

/** An organisation as rooming screens name it. */
export interface RoomingOrgRef {
	responsible_organization_id: string;
	/** null for organisations that are not NOCs (no country code). */
	code: string | null;
	name: string;
}

/** A reservation on a room (host and HOTEL views only — never in a NOC read). */
export interface RoomReservationRef {
	responsible_organization: RoomingOrgRef;
	sex: LodgingSex | null;
}

/** One taken bed slot on the floor map on the date. */
export interface FloorMapBed {
	bed_no: number;
	sex: LodgingSex;
	organization_code: string | null;
	checked_in: boolean;
}

export interface FloorMapRoom {
	lodging_room_id: string;
	block: string | null;
	floor: number;
	room_no: string;
	/** `formatRoomNo`. */
	room_label: string;
	beds: number;
	sex_restriction: LodgingSex | null;
	out_of_service: boolean;
	oos_note: string | null;
	reservation: RoomReservationRef | null;
	state: RoomState;
	/** Taken beds only; a slot 1..beds absent here is free. */
	occupants: FloorMapBed[];
}

/** `GET /lodging-buildings/:id/floor-map`. */
export interface FloorMap {
	building: LodgingBuildingRef;
	date: string;
	blocks: string[];
	block: string | null;
	rooms: FloorMapRoom[];
	/** Room counts by state, as values (`ok()` lower-cases all-caps keys). */
	states: Array<{ state: RoomState; count: number }>;
	truncated: boolean;
}

/** An assignment as every rooming / desk / record screen shows it. */
export interface RoomAssignmentSummary {
	room_assignment_id: string;
	building: LodgingBuildingRef;
	lodging_room_id: string;
	room_label: string;
	bed_no: number;
	sex: LodgingSex;
	/** First night, YYYY-MM-DD. */
	stay_from: string;
	/** Check-out day (exclusive upper bound), YYYY-MM-DD. */
	stay_to: string;
	stay_source: StaySource;
	checked_in_at: string | null;
	checked_in_via: CheckInVia | null;
	checked_out_at: string | null;
}

/** `GET /lodging-rooms/:id`: the room, its reservation and the occupants overlapping [date, ∞). */
export interface LodgingRoomDetail extends FloorMapRoom {
	building: LodgingBuildingRef;
	occupants_detail: Array<RoomAssignmentSummary & { ac_card_id: string; name: string; organization_code: string | null }>;
}

/** `POST /accommodation/reservations`. */
export interface ReserveRoomsResult {
	rooms: number;
	beds: number;
	/** Beds asked for that no free room could cover, per sex. */
	short: Array<{ sex: LodgingSex; beds: number }>;
}

export interface ReleaseReservationsResult {
	released: number;
	skipped: Array<{ lodging_room_id: string; reason: ReservationReleaseRefusal }>;
}

export interface ContingentStayDefaults {
	games_id: string;
	responsible_organization_id: string;
	arrive_on: string | null;
	depart_on: string | null;
}

/** One row of `GET /accommodation/contingents` (the Rooming table). */
export interface RoomingContingent {
	responsible_organization: RoomingOrgRef;
	approved: number;
	approved_male: number;
	approved_female: number;
	/** APPROVED with no gender: auto-assign skips them (`no_gender`). */
	approved_no_gender: number;
	reserved_beds_male: number;
	reserved_beds_female: number;
	/** Reserved rooms with no sex: either. */
	reserved_beds_any: number;
	with_bed: number;
	checked_in: number;
	without_bed: number;
	/**
	 * APPROVED people who need a bed (user, 2026-11-03: "only people arriving"):
	 * an ARRIVAL leg on record, or a bed already. The room gap counts these,
	 * not everyone approved — people with no arrival yet do not ask for rooms.
	 */
	need_room: number;
	need_room_male: number;
	need_room_female: number;
	/** Reserved beds − `need_room` (negative = short). */
	gap: number;
	stay_defaults: { arrive_on: string; depart_on: string } | null;
}

/** One person of `GET /accommodation/contingents/:org/people`. A NOC read carries only its own people. */
export interface RoomingPerson {
	ac_card_id: string;
	people_id: string;
	registration_no: number;
	accreditation_no: number | null;
	/** The organisation's country code (NOC), else null: the middle of `ACC-THA-000123`. */
	organization_code: string | null;
	name: string;
	gender: PersonGender | null;
	main_function: { main_function_id: string; code: string; name: string } | null;
	/** The card's function (sub function), when set. */
	sub_function: { sub_function_id: string; name: string } | null;
	/** Someone else changed this card or its person since this login last opened it (contracts `seen.ts`). */
	changed: boolean;
	/** Sport name, else HQ — auto-assign's team key. */
	team: string | null;
	photo_thumb_url: string | null;
	state: RoomingPersonState;
	/** The open assignment, else the latest one; null = never had a bed. */
	assignment: RoomAssignmentSummary | null;
	/** What `fn_room_stay` gives now (null = no dates). Differs from `assignment` → STAY_DIFFERS. */
	derived_stay: { stay_from: string; stay_to: string; source: StaySource } | null;
	flags: AccommodationFlag[];
}

export type RoomingPeoplePage = Paginated<RoomingPerson>;

/** `POST /accommodation/auto-assign`. */
/** One proposed bed of a room suggestion. `stay_to` = the morning they leave (exclusive, as the rooming list prints it). */
export interface RoomAutoAssignPick {
	ac_card_id: string;
	name: string;
	responsible_organization_id: string;
	building_name: string;
	room_no_display: string;
	bed_no: number;
	stay_from: string;
	stay_to: string;
}

/** `POST /accommodation/auto-assign/suggest`: nothing was saved. */
export interface RoomAutoAssignSuggestion {
	/** ≤ AUTO_ASSIGN_PICKS_MAX listed; `pick_count` is the full number. */
	picks: RoomAutoAssignPick[];
	pick_count: number;
	skipped: RoomAutoAssignResult["skipped"];
	skipped_count: number;
	by_organization: Array<{ responsible_organization_id: string; assigned: number; skipped: number }>;
	/** Per organisation: send back unchanged to `POST /accommodation/auto-assign`. */
	fingerprints: Record<string, string>;
}

export interface RoomAutoAssignResult {
	/** Organisations NOT saved because their picks moved since the review (someone else assigned a bed, travel changed…): review again. */
	changed: string[];
	assigned: number;
	/** ≤ AUTO_ASSIGN_SKIPPED_MAX listed; `skipped_count` is the full number. */
	skipped: Array<{ ac_card_id: string; name: string; responsible_organization_id: string; reason: AutoAssignSkipReason }>;
	skipped_count: number;
	by_organization: Array<{ responsible_organization_id: string; assigned: number; skipped: number }>;
}

/** `GET /accommodation/summary` (overview tiles and needs-attention). */
export interface AccommodationSummary {
	date: string;
	beds: number;
	out_of_service_beds: number;
	occupied_beds: number;
	in_house: number;
	/** First night = date, not checked in yet. */
	arriving: number;
	/** First night before date, never checked in. */
	overdue: number;
	/** Last night's morning = date, still in. */
	departing: number;
	/** APPROVED in scope without an open assignment; null for HOTEL (it sees no cards). */
	without_bed: number | null;
	/** Open assignments whose dates were set by hand; the per-row travel comparison lives on the people list. */
	manual_stays: number;
	withdrawn_in_house: number;
	open_tickets: Array<{ priority: string; count: number }>;
	buildings: Array<{ lodging_building_id: string; name: string; beds: number; occupied_beds: number }>;
}

/** A guest on the desk screens (arrivals, departures, in house, lookup). HOTEL-safe: no ID number, birth date, phone or documents (§0b Q3). */
export interface DeskGuest {
	room_assignment_id: string;
	ac_card_id: string;
	accreditation_no: number | null;
	name: string;
	gender: PersonGender | null;
	organization_code: string | null;
	category: string | null;
	photo_thumb_url: string | null;
	assignment: RoomAssignmentSummary;
	/** Arrival: eta and its window (ISO, computed). Departure: `check_out_by`. */
	eta: string | null;
	window_from: string | null;
	window_to: string | null;
	check_out_by: string | null;
	flags: AccommodationFlag[];
}

/** One group of `GET /accommodation/arrivals` / `/departures`. */
export interface StayGroup {
	group_key: string;
	kind: StayGroupKind;
	flight: { flight_id: string; carrier_code: string; flight_number: string; status: FlightStatus; scheduled_at: string | null; expected_at: string | null } | null;
	entry_point: { entry_point_id: string; code: string; name: string } | null;
	service_no: string | null;
	/** Arrivals: eta and the window [eta − ETA_EARLY_MINUTES, eta + ETA_LATE_MINUTES]. Departures: null. */
	eta: string | null;
	window_from: string | null;
	window_to: string | null;
	/** Departures: the M10 pickup, else expected_at − lead − drive, else the building's check-out time. Arrivals: null. */
	check_out_by: string | null;
	late: boolean;
	total: number;
	/** Checked in (arrivals) or checked out (departures). */
	done: number;
	organizations: string[];
	organizations_more: number;
}

export interface StayGroupsResponse {
	date: string;
	groups: StayGroup[];
	/** Arrivals only: earlier first nights, never checked in. */
	overdue: number;
	truncated: boolean;
}

export type DeskGuestPage = Paginated<DeskGuest>;

/** One person arriving without a room (`GET /accommodation/needs-room`). Host screens only. */
export interface NeedsRoomPerson {
	ac_card_id: string;
	card_status: AcCardStatus;
	/** The printed name (accreditation name, else the person's). */
	name: string;
	gender: PersonGender | null;
	/** `REG-THA-000123` (`formatRegistrationNo`); `ACC-…` once approved. */
	registration_no_display: string;
	accreditation_no_display: string | null;
	responsible_organization: RoomingOrgRef;
	category: string | null;
	arrival: {
		travel_leg_id: string;
		mode: TravelMode;
		travel_date: string;
		/** Null = no time yet. */
		expected_at: string | null;
		flight: FlightSummary | null;
		service_no: string | null;
		entry_point: EntryPointRef;
		/** The A&D journey (`group_key` of `/travel/journeys`). */
		journey_key: string;
	};
	/**
	 * The stay `fn_room_stay` derives for them (travel, else the contingent's
	 * defaults, else the games' dates) against the building a bed would most
	 * likely be in — the organisation's first reserved building, else the first
	 * active one. `stay_from` is the first night: a 02:00 arrival keeps the
	 * night before. Null when no building exists yet.
	 */
	stay: { stay_from: string; stay_to: string; source: StaySource } | null;
}

/** `GET /accommodation/needs-room` — keyset. The first page carries the totals and the per-organisation counts. */
export interface NeedsRoomPage {
	date: string;
	timezone: string;
	items: NeedsRoomPerson[];
	next_cursor: string | null;
	/** First page only: both states' totals for the day (the tabs). */
	totals: { needs_room: number; not_approved: number } | null;
	/** First page only: the state's people per organisation (the chips, each with Auto-assign), largest first. */
	by_organization: Array<{ responsible_organization: RoomingOrgRef; count: number }> | null;
}

/** One day of `GET /accommodation/needs-room/day-counts`. */
export interface NeedsRoomDayCount {
	/** YYYY-MM-DD, the games' zone. */
	date: string;
	needs_room: number;
	not_approved: number;
}

/** `GET /accommodation/needs-room/day-counts` — only the days with anyone arriving without a room, ascending. */
export interface NeedsRoomDayCounts {
	days: NeedsRoomDayCount[];
	timezone: string;
}

/** `POST /room-assignments/check`. */
export interface RoomCheckResult {
	action: RoomCheckAction;
	done: string[];
	skipped: Array<{ room_assignment_id: string; reason: RoomCheckRefusal }>;
}

/** `GET /room-assignments/lookup`: the card in scope, whether it may check in, and its open assignment. */
export interface DeskLookup {
	ac_card_id: string;
	accreditation_no: number | null;
	name: string;
	card_ready: boolean;
	guest: DeskGuest | null;
}

/** `GET /accreditations/:id` → `accommodation` (M11, one LEFT JOIN LATERAL in the record query). */
export interface AccreditationAccommodation {
	state: RoomingPersonState;
	assignment: RoomAssignmentSummary | null;
	flags: AccommodationFlag[];
}

/**
 * One item of `GET /accommodation/contingents/:org/rooms` (`{ items }`): a room
 * where the caller's own in-scope people of the organisation hold an open
 * assignment — the NOC's "report a room problem" picker. Rooms only: no
 * occupant, no count, nothing of another contingent (NOC privacy).
 */
export interface ContingentRoomRef {
	lodging_room_id: string;
	lodging_building_id: string;
	building_name: string;
	/** `formatRoomNo`. */
	room_no_display: string;
}

/**
 * `GET /room-assignments/options?ac_card_id=&games_id=&lodging_building_id=`
 * — the Assign / Move room picker (`{ items: RoomOption[] }`). Empty when the
 * actor may not manage the card, or it has no gender / no dates.
 */
export const roomOptionsQuery = z.object({
	ac_card_id: uuid,
	games_id: uuid,
	lodging_building_id: uuid.optional(),
});
export type RoomOptionsQuery = z.output<typeof roomOptionsQuery>;

export interface RoomOption {
	lodging_room_id: string;
	room_label: string;
	building: { lodging_building_id: string; name: string };
	beds: number;
	/** Beds held by someone else over (part of) the stay. */
	taken: number;
}
